import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const ProjectTable = ({ projects, onProjectAction, onSort, sortConfig }) => {
  const [selectedProjects, setSelectedProjects] = useState([]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success/10 text-success border-success/20';
      case 'planning':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'on-hold':
        return 'bg-secondary/10 text-secondary border-secondary/20';
      case 'completed':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'cancelled':
        return 'bg-error/10 text-error border-error/20';
      default:
        return 'bg-muted/10 text-muted-foreground border-muted/20';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Ativo';
      case 'planning':
        return 'Planejamento';
      case 'on-hold':
        return 'Pausado';
      case 'completed':
        return 'Concluído';
      case 'cancelled':
        return 'Cancelado';
      default:
        return 'Indefinido';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-error';
      case 'medium':
        return 'text-warning';
      case 'low':
        return 'text-success';
      default:
        return 'text-muted-foreground';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('pt-BR');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(amount);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedProjects(projects?.map(p => p?.id));
    } else {
      setSelectedProjects([]);
    }
  };

  const handleSelectProject = (projectId, checked) => {
    if (checked) {
      setSelectedProjects(prev => [...prev, projectId]);
    } else {
      setSelectedProjects(prev => prev?.filter(id => id !== projectId));
    }
  };

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) {
      return 'ArrowUpDown';
    }
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const handleBulkAction = (action) => {
    if (selectedProjects?.length === 0) return;
    
    selectedProjects?.forEach(projectId => {
      onProjectAction(action, projectId);
    });
    setSelectedProjects([]);
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Bulk Actions Bar */}
      {selectedProjects?.length > 0 && (
        <div className="bg-primary/5 border-b border-border px-6 py-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground">
              {selectedProjects?.length} projeto(s) selecionado(s)
            </span>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('activate')}
                iconName="Play"
                iconPosition="left"
              >
                Ativar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('pause')}
                iconName="Pause"
                iconPosition="left"
              >
                Pausar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('export')}
                iconName="Download"
                iconPosition="left"
              >
                Exportar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedProjects([])}
                iconName="X"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 px-6 py-4">
                <input
                  type="checkbox"
                  checked={selectedProjects?.length === projects?.length && projects?.length > 0}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                  className="rounded border-border"
                />
              </th>
              <th className="text-left px-6 py-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSort('name')}
                  className="font-medium text-muted-foreground hover:text-foreground"
                >
                  Projeto
                  <Icon name={getSortIcon('name')} size={16} className="ml-1" />
                </Button>
              </th>
              <th className="text-left px-6 py-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSort('client')}
                  className="font-medium text-muted-foreground hover:text-foreground"
                >
                  Cliente
                  <Icon name={getSortIcon('client')} size={16} className="ml-1" />
                </Button>
              </th>
              <th className="text-left px-6 py-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSort('startDate')}
                  className="font-medium text-muted-foreground hover:text-foreground"
                >
                  Data Início
                  <Icon name={getSortIcon('startDate')} size={16} className="ml-1" />
                </Button>
              </th>
              <th className="text-left px-6 py-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSort('status')}
                  className="font-medium text-muted-foreground hover:text-foreground"
                >
                  Status
                  <Icon name={getSortIcon('status')} size={16} className="ml-1" />
                </Button>
              </th>
              <th className="text-left px-6 py-4">Técnicos</th>
              <th className="text-left px-6 py-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSort('progress')}
                  className="font-medium text-muted-foreground hover:text-foreground"
                >
                  Progresso
                  <Icon name={getSortIcon('progress')} size={16} className="ml-1" />
                </Button>
              </th>
              <th className="text-left px-6 py-4">Valor</th>
              <th className="text-right px-6 py-4">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {projects?.map((project) => (
              <tr key={project?.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedProjects?.includes(project?.id)}
                    onChange={(e) => handleSelectProject(project?.id, e?.target?.checked)}
                    className="rounded border-border"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className={`w-2 h-8 rounded-full ${getPriorityColor(project?.priority)}`}></div>
                    <div>
                      <p className="font-medium text-foreground">{project?.name}</p>
                      <p className="text-sm text-muted-foreground">{project?.code}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-2">
                    <Image
                      src={project?.client?.logo}
                      alt={`Logo da empresa ${project?.client?.name}`}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-medium text-foreground">{project?.client?.name}</p>
                      <p className="text-sm text-muted-foreground">{project?.client?.contact}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div>
                    <p className="text-foreground">{formatDate(project?.startDate)}</p>
                    <p className="text-sm text-muted-foreground">
                      Fim: {formatDate(project?.endDate)}
                    </p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(project?.status)}`}>
                    {getStatusLabel(project?.status)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex -space-x-2">
                    {project?.technicians?.slice(0, 3)?.map((tech, index) => (
                      <Image
                        key={tech?.id}
                        src={tech?.avatar}
                        alt={`Foto do técnico ${tech?.name}`}
                        className="w-8 h-8 rounded-full border-2 border-background object-cover"
                        title={tech?.name}
                      />
                    ))}
                    {project?.technicians?.length > 3 && (
                      <div className="w-8 h-8 rounded-full bg-muted border-2 border-background flex items-center justify-center">
                        <span className="text-xs font-medium text-muted-foreground">
                          +{project?.technicians?.length - 3}
                        </span>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="w-full">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-foreground">{project?.progress}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${project?.progress}%` }}
                      ></div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div>
                    <p className="font-medium text-foreground">{formatCurrency(project?.value)}</p>
                    <p className="text-sm text-muted-foreground">
                      {project?.serviceOrders} OS
                    </p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end space-x-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onProjectAction('view', project?.id)}
                      title="Ver detalhes"
                    >
                      <Icon name="Eye" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onProjectAction('edit', project?.id)}
                      title="Editar projeto"
                    >
                      <Icon name="Edit" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onProjectAction('orders', project?.id)}
                      title="Ordens de serviço"
                    >
                      <Icon name="ClipboardList" size={16} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden divide-y divide-border">
        {projects?.map((project) => (
          <div key={project?.id} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={selectedProjects?.includes(project?.id)}
                  onChange={(e) => handleSelectProject(project?.id, e?.target?.checked)}
                  className="rounded border-border"
                />
                <div className={`w-1 h-12 rounded-full ${getPriorityColor(project?.priority)}`}></div>
                <div>
                  <h3 className="font-medium text-foreground">{project?.name}</h3>
                  <p className="text-sm text-muted-foreground">{project?.code}</p>
                </div>
              </div>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(project?.status)}`}>
                {getStatusLabel(project?.status)}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Cliente</p>
                <div className="flex items-center space-x-2">
                  <Image
                    src={project?.client?.logo}
                    alt={`Logo da empresa ${project?.client?.name}`}
                    className="w-6 h-6 rounded-full object-cover"
                  />
                  <p className="text-sm font-medium text-foreground">{project?.client?.name}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Valor</p>
                <p className="text-sm font-medium text-foreground">{formatCurrency(project?.value)}</p>
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">Progresso</span>
                <span className="text-xs font-medium text-foreground">{project?.progress}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${project?.progress}%` }}
                ></div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex -space-x-1">
                {project?.technicians?.slice(0, 3)?.map((tech) => (
                  <Image
                    key={tech?.id}
                    src={tech?.avatar}
                    alt={`Foto do técnico ${tech?.name}`}
                    className="w-6 h-6 rounded-full border border-background object-cover"
                  />
                ))}
                {project?.technicians?.length > 3 && (
                  <div className="w-6 h-6 rounded-full bg-muted border border-background flex items-center justify-center">
                    <span className="text-xs font-medium text-muted-foreground">
                      +{project?.technicians?.length - 3}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onProjectAction('view', project?.id)}
                >
                  <Icon name="Eye" size={16} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onProjectAction('edit', project?.id)}
                >
                  <Icon name="Edit" size={16} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onProjectAction('orders', project?.id)}
                >
                  <Icon name="ClipboardList" size={16} />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Empty State */}
      {projects?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="FolderOpen" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            Nenhum projeto encontrado
          </h3>
          <p className="text-muted-foreground mb-4">
            Não há projetos que correspondam aos filtros aplicados.
          </p>
          <Button
            variant="outline"
            onClick={() => onProjectAction('create')}
            iconName="Plus"
            iconPosition="left"
          >
            Criar Primeiro Projeto
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProjectTable;